import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  ArrowPathIcon,
  ClockIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { generateRule, createRule, fetchTenants } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import Badge from './ui/Badge';
import { confidenceTone } from './ui/badgeTones';
import MspScopeSelect from './ui/MspScopeSelect';

const SEVERITY_OPTIONS = ['critical', 'high', 'medium', 'low', 'informational'];

const BLUE_ALLIANCE_TENANT_ID = '752b8092-f529-4eff-a6e8-179e3309ec1e';

// Rule-specific: only rule_new_country_signin reads suppressed_event_country_codes today.
const NEW_COUNTRY_SIGNIN_FN = 'rule_new_country_signin';
// The batched insider-threat rule coalesces per-tenant entries into one config, so MSP
// codes there would apply batch-wide with first-entry semantics (handoff). Hide MSP scope.
const MAILBOX_NON_OWNER_FN = 'rule_mailbox_accessed_by_non_owner';
const COMMON_COUNTRY_CODES = [
  'US', 'CA', 'GB', 'IE', 'AU', 'NZ', 'DE', 'FR', 'ES', 'IT', 'NL', 'BE',
  'CH', 'AT', 'SE', 'NO', 'DK', 'FI', 'PL', 'PT', 'IN', 'JP', 'KR', 'SG',
  'HK', 'CN', 'MX', 'BR', 'AR', 'ZA', 'AE', 'IL',
];

const IP_FLAG_OPTIONS = [
  { value: 'detections_tor', label: 'Tor exit node' },
  { value: 'detections_vpn', label: 'VPN' },
  { value: 'detections_proxy', label: 'Proxy' },
  { value: 'detections_compromised', label: 'Compromised / botnet' },
  { value: 'detections_anonymous', label: 'Anonymous' },
  { value: 'detections_hosting', label: 'Hosting / datacenter' },
  { value: 'detections_scraper', label: 'Scraper' },
];

const EVENT_TYPE_OPTIONS = ['signin', 'graph_signin'];

const LOG_TYPE_OPTIONS = [
  { value: 'signin', label: 'Sign-in' },
  { value: 'graph_signin', label: 'Graph Sign-in' },
  { value: 'directory_audit', label: 'Directory Audit' },
  { value: 'exchange', label: 'Exchange' },
  { value: 'sharepoint', label: 'SharePoint' },
  { value: 'general', label: 'General' },
  { value: 'dlp', label: 'DLP' },
  { value: 'risk_detection', label: 'Risk Detection' },
  { value: 'risky_user', label: 'Risky User' },
  { value: 'ca_policy', label: 'CA Policy' },
  { value: 'security_alert', label: 'Security Alert' },
  { value: 'security_incident', label: 'Security Incident' },
  { value: 'secure_score', label: 'Secure Score' },
  { value: 'azure_activity', label: 'Azure Activity' },
  { value: 'message_trace', label: 'Message Trace' },
  { value: 'url_click', label: 'URL Click' },
];

const MATCH_OPERATORS = ['equals', 'contains', 'starts_with', 'not_equals'];

const BASELINE_WINDOW_OPTIONS = [
  { value: '7d', label: '7 days (default)' },
  { value: '30d', label: '30 days' },
];

// JSON path syntax hint for log_type_match fields (Databricks handoff 2026-04-08).
// Returns null when we don't have a specific recommendation for the log type.
function getFieldSyntaxHint(logType) {
  const GRAPH = new Set(['risk_detection','graph_signin','directory_audit','risky_user','security_alert','security_incident','secure_score','ca_policy']);
  const O365  = new Set(['exchange','sharepoint','signin','general','dlp','message_trace']);
  if (GRAPH.has(logType))     return { api: 'Graph API',      caseStyle: 'camelCase',  example: '$.riskLevel' };
  if (O365.has(logType))      return { api: 'O365 Audit',     caseStyle: 'PascalCase', example: '$.ClientInfoString' };
  if (logType === 'azure_activity') return { api: 'Azure Activity', caseStyle: 'camelCase',  example: '$.httpRequest.clientIpAddress' };
  if (logType === 'url_click')      return { api: 'URL Click',      caseStyle: 'PascalCase', example: '$.Url' };
  return null;
}

const INPUT_LIMITS = { natural_language: 5000, yaml: 20000 };

const CONFIDENCE_LABEL = {
  high: 'High confidence',
  medium: 'Medium confidence — review carefully',
  low: 'Low confidence — verify all fields',
};

function parseParametersJson(json) {
  try { return JSON.parse(json || '{}'); } catch { return {}; }
}

export default function CreateRuleDrawer({ isOpen, onClose, onSuccess, cloneSource = null }) {
  const { user } = useAuth();
  // State machine: input_form | loading | draft_review | saving | unsupported_panel | existing_function_panel | error_panel | rate_limited
  const [phase, setPhase] = useState('input_form');

  // Input form state
  const [inputText, setInputText] = useState('');
  const [inputType, setInputType] = useState('natural_language');
  const [workload, setWorkload] = useState('');

  // Generate response state
  const [generateResponse, setGenerateResponse] = useState(null);

  // Editable draft fields
  const [ruleName, setRuleName] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [ruleType, setRuleType] = useState('ip_flag_match');
  const [cooldownMinutes, setCooldownMinutes] = useState(60);
  const [suggestedSlug, setSuggestedSlug] = useState('');
  // ip_flag_match params
  const [ipFlag, setIpFlag] = useState('');
  const [eventTypes, setEventTypes] = useState(['signin', 'graph_signin']);
  // log_type_match params
  const [logType, setLogType] = useState('');
  const [matchConditions, setMatchConditions] = useState([]);
  // baseline_deviation params
  const [baselineField, setBaselineField] = useState('country_list');
  // operation_baseline_deviation params
  const [opBaselineLogType, setOpBaselineLogType] = useState('');
  const [opBaselineOperation, setOpBaselineOperation] = useState('');
  const [opBaselineField] = useState('signin_ip_list');
  // python_function_config: pipeline-defined params stored as raw JSON
  const [rawParamsJson, setRawParamsJson] = useState('{}');
  const [pythonFunctionName, setPythonFunctionName] = useState('');

  // Suppression toggles
  const [skipIfEntraJoined, setSkipIfEntraJoined] = useState(false);
  const [skipIfCompliantDevice, setSkipIfCompliantDevice] = useState(false);
  const [skipIfDrmmMatch, setSkipIfDrmmMatch] = useState(false);
  const [skipIfIntuneManaged, setSkipIfIntuneManaged] = useState(false);
  const [skipIfInTenantBaseline, setSkipIfInTenantBaseline] = useState(false);
  const [skipIfInUserBaseline, setSkipIfInUserBaseline] = useState(false);
  const [skipIfProviderInUserBaseline, setSkipIfProviderInUserBaseline] = useState(false);
  const [providerBaselineMinIps, setProviderBaselineMinIps] = useState(2);
  const [skipIfCtapTrust, setSkipIfCtapTrust] = useState(false);
  const [skipIfInMailboxPairAllowlist, setSkipIfInMailboxPairAllowlist] = useState(false);
  const [skipIfTeamsPhoneEnrollment, setSkipIfTeamsPhoneEnrollment] = useState(false);
  const [skipIfInFileHashAllowlist, setSkipIfInFileHashAllowlist] = useState(false);
  const [skipIfInDefenderPassthroughIpAllowlist, setSkipIfInDefenderPassthroughIpAllowlist] = useState(false);
  const [skipIfInUserAppPairAllowlist, setSkipIfInUserAppPairAllowlist] = useState(false);
  const [skipIfInGroupAppAllowlist, setSkipIfInGroupAppAllowlist] = useState(false);
  const [skipIpv6PrefixMatch, setSkipIpv6PrefixMatch] = useState(false);
  const [overrideIfMaliciousIp, setOverrideIfMaliciousIp] = useState(false);
  // dedup_on_event_id — one alert per distinct source event vs. one per user per cooldown
  const [dedupOnEventId, setDedupOnEventId] = useState(false);

  // Baseline-read knob — lives inside parametersJson, default to current behavior if absent.
  // `baseline_ip_column` is auto-derived from window=30d + skipIpv6PrefixMatch; no separate picker.
  const [baselineWindow, setBaselineWindow] = useState('7d');

  // Rule scope: tenant UUIDs to include/exclude (top-level) + IP providers to exclude (inside parametersJson)
  const [includedTenantIds, setIncludedTenantIds] = useState([]);
  const [excludedTenantIds, setExcludedTenantIds] = useState([]);
  const [includedMspCodes, setIncludedMspCodes] = useState([]);   // uppercase MSP codes
  const [excludedMspCodes, setExcludedMspCodes] = useState([]);   // uppercase MSP codes
  const [excludedIpProviders, setExcludedIpProviders] = useState([]);
  const [providerInput, setProviderInput] = useState('');
  const [tenantList, setTenantList] = useState([]);

  // rule_new_country_signin specific: suppressed_event_country_codes inside parametersJson
  const [suppressedCountryCodes, setSuppressedCountryCodes] = useState([]);
  const [countryInput, setCountryInput] = useState('');

  // Error state
  const [errorMessage, setErrorMessage] = useState('');
  const [saveError, setSaveError] = useState('');

  // Rate limit state
  const [rateLimitSeconds, setRateLimitSeconds] = useState(0);
  const rateLimitTimer = useRef(null);

  // Countdown timer for rate limit
  useEffect(() => {
    if (rateLimitSeconds > 0) {
      rateLimitTimer.current = setInterval(() => {
        setRateLimitSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(rateLimitTimer.current);
            setPhase('input_form');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(rateLimitTimer.current);
    }
  }, [rateLimitSeconds]);

  // Fetch tenant list once when drawer opens (for the Exclude Tenants multi-select)
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    fetchTenants()
      .then((list) => { if (!cancelled) setTenantList(Array.isArray(list) ? list : []); })
      .catch(() => { if (!cancelled) setTenantList([]); });
    return () => { cancelled = true; };
  }, [isOpen]);

  // Reset scope state whenever drawer closes (so next open starts clean unless it's a clone)
  useEffect(() => {
    if (!isOpen) {
      setIncludedTenantIds([]);
      setExcludedTenantIds([]);
      setExcludedIpProviders([]);
      setProviderInput('');
      setBaselineWindow('7d');
      setSuppressedCountryCodes([]);
      setCountryInput('');
    }
  }, [isOpen]);

  // When opened with a cloneSource, skip the AI input form and pre-fill the draft
  useEffect(() => {
    if (isOpen && cloneSource) {
      const newName = `Copy of ${cloneSource.ruleName}`;
      const slug = newName
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .substring(0, 30)
        .replace(/-$/, '');
      populateDraftFields({
        ruleName: newName,
        description: cloneSource.description || `Cloned from ${cloneSource.ruleId}`,
        severity: cloneSource.severity,
        ruleType: cloneSource.ruleType,
        parametersJson: cloneSource.parametersJson || '{}',
        cooldownMinutes: cloneSource.cooldownMinutes ?? 60,
        suggestedSlug: slug,
      });
      setSkipIfEntraJoined(cloneSource.skipIfEntraJoined ?? false);
      setSkipIfCompliantDevice(cloneSource.skipIfCompliantDevice ?? false);
      setSkipIfDrmmMatch(cloneSource.skipIfDrmmMatch ?? false);
      setSkipIfIntuneManaged(cloneSource.skipIfIntuneManaged ?? false);
      setSkipIfInTenantBaseline(cloneSource.skipIfInTenantBaseline ?? false);
      setSkipIfInUserBaseline(cloneSource.skipIfInUserBaseline ?? false);
      setSkipIfProviderInUserBaseline(cloneSource.skipIfProviderInUserBaseline ?? false);
      setSkipIfCtapTrust(cloneSource.skipIfCtapTrust ?? false);
      setSkipIfInMailboxPairAllowlist(cloneSource.skipIfInMailboxPairAllowlist ?? false);
      setSkipIfTeamsPhoneEnrollment(cloneSource.skipIfTeamsPhoneEnrollment ?? false);
      setSkipIfInFileHashAllowlist(cloneSource.skipIfInFileHashAllowlist ?? false);
      setSkipIfInDefenderPassthroughIpAllowlist(cloneSource.skipIfInDefenderPassthroughIpAllowlist ?? false);
      setSkipIfInUserAppPairAllowlist(cloneSource.skipIfInUserAppPairAllowlist ?? false);
      setSkipIfInGroupAppAllowlist(cloneSource.skipIfInGroupAppAllowlist ?? false);
      setSkipIpv6PrefixMatch(cloneSource.skipIpv6PrefixMatch ?? false);
      setOverrideIfMaliciousIp(cloneSource.overrideIfMaliciousIp ?? false);
      setDedupOnEventId(cloneSource.dedupOnEventId ?? false);
      // Rule scope hydration from clone
      const cloneExcludeCsv = (cloneSource.excludedTenantIds || '').toString();
      setExcludedTenantIds(cloneExcludeCsv ? cloneExcludeCsv.split(',').map((s) => s.trim()).filter(Boolean) : []);
      const cloneIncludeCsv = (cloneSource.includedTenantIds || '').toString();
      setIncludedTenantIds(cloneIncludeCsv ? cloneIncludeCsv.split(',').map((s) => s.trim()).filter(Boolean) : []);
      const cloneExcludeMspCsv = (cloneSource.excludedMspCodes || '').toString();
      setExcludedMspCodes(cloneExcludeMspCsv ? cloneExcludeMspCsv.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean) : []);
      const cloneIncludeMspCsv = (cloneSource.includedMspCodes || '').toString();
      setIncludedMspCodes(cloneIncludeMspCsv ? cloneIncludeMspCsv.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean) : []);
      setPythonFunctionName(cloneSource.pythonFunctionName || '');
      setPhase('draft_review');
    }
  }, [isOpen, cloneSource]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetToInput = () => {
    setPhase('input_form');
    setGenerateResponse(null);
    setSaveError('');
    setErrorMessage('');
  };

  const resetAll = () => {
    setInputText('');
    setInputType('natural_language');
    setWorkload('');
    setRawParamsJson('{}');
    setPythonFunctionName('');
    resetToInput();
    setRateLimitSeconds(0);
    if (rateLimitTimer.current) clearInterval(rateLimitTimer.current);
  };

  const handleClose = () => {
    resetAll();
    onClose();
  };

  const populateDraftFields = (draft) => {
    setRuleName(draft.ruleName || '');
    setDescription(draft.description || '');
    setSeverity(draft.severity || 'medium');
    setRuleType(draft.ruleType || 'ip_flag_match');
    setCooldownMinutes(draft.cooldownMinutes ?? 60);
    setSuggestedSlug(draft.suggestedSlug || '');

    const params = parseParametersJson(draft.parametersJson);
    setExcludedIpProviders(Array.isArray(params.excluded_ip_providers) ? params.excluded_ip_providers : []);
    setBaselineWindow(params.baseline_window === '30d' ? '30d' : '7d');
    const draftMinIps = Number(params.provider_baseline_min_ips);
    setProviderBaselineMinIps(Number.isFinite(draftMinIps) && draftMinIps >= 1 ? draftMinIps : 2);
    // suppressed_event_country_codes — preserved on clone, hidden in UI unless
    // python_function_name === rule_new_country_signin.
    const rawCountries = Array.isArray(params.suppressed_event_country_codes)
      ? params.suppressed_event_country_codes
      : [];
    const cleanedCountries = [];
    const seenCountries = new Set();
    for (const code of rawCountries) {
      if (typeof code !== 'string') continue;
      const upper = code.trim().toUpperCase();
      if (upper.length !== 2 || seenCountries.has(upper)) continue;
      seenCountries.add(upper);
      cleanedCountries.push(upper);
    }
    setSuppressedCountryCodes(cleanedCountries);
    setCountryInput('');
    if (draft.ruleType === 'log_type_match') {
      setLogType(params.log_type || '');
      setMatchConditions(Array.isArray(params.match_conditions) ? params.match_conditions : []);
      setIpFlag('');
      setEventTypes(['signin', 'graph_signin']);
      setBaselineField('country_list');
    } else if (draft.ruleType === 'baseline_deviation') {
      setBaselineField(params.baseline_field || 'country_list');
      setIpFlag('');
      setLogType('');
      setMatchConditions([]);
      setEventTypes(Array.isArray(params.event_types) && params.event_types.length > 0 ? params.event_types : ['signin']);
    } else if (draft.ruleType === 'operation_baseline_deviation') {
      setOpBaselineLogType(params.log_type || '');
      // Pipeline reads plural `operations` (array). Older drafts/clones used
      // singular `operation` — fall back so legacy data hydrates cleanly.
      const opsArr = Array.isArray(params.operations)
        ? params.operations
        : (params.operation ? [params.operation] : []);
      setOpBaselineOperation(opsArr.join(', '));
      setIpFlag('');
      setLogType('');
      setMatchConditions([]);
      setEventTypes(['signin', 'graph_signin']);
      setBaselineField('country_list');
    } else if (draft.ruleType === 'python_function_config') {
      setRawParamsJson(draft.parametersJson || '{}');
      setIpFlag('');
      setLogType('');
      setMatchConditions([]);
      setEventTypes(['signin', 'graph_signin']);
      setBaselineField('country_list');
    } else {
      setIpFlag(params.ip_flag || '');
      setEventTypes(params.event_types || ['signin', 'graph_signin']);
      setLogType('');
      setMatchConditions([]);
    }
  };

  const handleGenerate = async () => {
    const trimmed = inputText.trim();
    if (!trimmed) return;

    const limit = INPUT_LIMITS[inputType];
    if (trimmed.length > limit) {
      setErrorMessage(`Input exceeds ${limit.toLocaleString()} character limit (currently ${trimmed.length.toLocaleString()}).`);
      setPhase('error_panel');
      return;
    }

    setPhase('loading');
    setErrorMessage('');
    try {
      const resp = await generateRule(trimmed, inputType, workload || null);
      setGenerateResponse(resp);

      const outcomeType = resp?.outcome?.outcomeType;
      if (outcomeType === 'draft_rule') {
        populateDraftFields(resp.outcome.ruleDraft);
        setPhase('draft_review');
      } else if (outcomeType === 'unsupported_request') {
        setPhase('unsupported_panel');
      } else if (outcomeType === 'requires_existing_python_function') {
        setPhase('existing_function_panel');
      } else {
        setErrorMessage(resp?.serverValidation?.errors?.[0] || 'Rule generation failed.');
        setPhase('error_panel');
      }
    } catch (err) {
      if (err.status === 429) {
        // Parse retry time from error body or default to 30 min
        const retryMatch = err.message?.match(/(\d+)/);
        const retrySec = retryMatch ? parseInt(retryMatch[1], 10) : 1800;
        setRateLimitSeconds(retrySec);
        setPhase('rate_limited');
      } else {
        setErrorMessage(err.message || 'Rule generation failed. Please try again.');
        setPhase('error_panel');
      }
    }
  };

  const buildParametersJson = () => {
    // Shared: stamp scope + baseline-read keys onto a typed params object.
    // Baseline keys are only written when non-default, so existing rule JSON stays clean.
    const stampScope = (p) => {
      if (skipIfInTenantBaseline) p.suppression_tenant_id = BLUE_ALLIANCE_TENANT_ID;
      if (excludedIpProviders.length > 0) p.excluded_ip_providers = excludedIpProviders;
      // baseline_window only meaningful when user-baseline suppression is on.
      // baseline_ip_column is auto-derived from the IPv6 /64 toggle when the 30-day window is picked.
      if (skipIfInUserBaseline && baselineWindow === '30d') {
        p.baseline_window = '30d';
        if (skipIpv6PrefixMatch) {
          p.baseline_ip_column = 'signin_ip_prefix_list';
        }
      }
      // provider_baseline_min_ips only written when the provider-in-user-baseline tier is on
      // AND the value is non-default (pipeline defaults a missing key to 2).
      const minIps = Number(providerBaselineMinIps);
      if (skipIfProviderInUserBaseline && Number.isFinite(minIps) && minIps >= 1 && minIps !== 2) {
        p.provider_baseline_min_ips = minIps;
      }
      // Picker is gated on python_function_name === rule_new_country_signin, so for any other
      // rule the array is empty and this is a no-op. Stamped from every structured branch.
      if (suppressedCountryCodes.length > 0) {
        p.suppressed_event_country_codes = suppressedCountryCodes;
      }
      return p;
    };
    if (ruleType === 'python_function_config') {
      // Pipeline-defined JSON. Merge scope keys if the JSON is a plain object.
      try {
        const parsed = JSON.parse(rawParamsJson);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          if (skipIfInTenantBaseline) parsed.suppression_tenant_id = BLUE_ALLIANCE_TENANT_ID;
          if (excludedIpProviders.length > 0) {
            parsed.excluded_ip_providers = excludedIpProviders;
          } else if ('excluded_ip_providers' in parsed) {
            delete parsed.excluded_ip_providers;
          }
          if (skipIfInUserBaseline && baselineWindow === '30d') {
            parsed.baseline_window = '30d';
          } else if ('baseline_window' in parsed) {
            delete parsed.baseline_window;
          }
          if (skipIfInUserBaseline && baselineWindow === '30d' && skipIpv6PrefixMatch) {
            parsed.baseline_ip_column = 'signin_ip_prefix_list';
          } else if ('baseline_ip_column' in parsed) {
            delete parsed.baseline_ip_column;
          }
          const cfgMinIps = Number(providerBaselineMinIps);
          if (skipIfProviderInUserBaseline && Number.isFinite(cfgMinIps) && cfgMinIps >= 1 && cfgMinIps !== 2) {
            parsed.provider_baseline_min_ips = cfgMinIps;
          } else if ('provider_baseline_min_ips' in parsed) {
            delete parsed.provider_baseline_min_ips;
          }
          // suppressed_event_country_codes — picker is gated on python_function_name,
          // so for non-rule_new_country_signin functions the array stays empty and
          // any pre-existing key (from clone) is preserved by the textarea path.
          if (suppressedCountryCodes.length > 0) {
            parsed.suppressed_event_country_codes = suppressedCountryCodes;
          } else if ('suppressed_event_country_codes' in parsed) {
            delete parsed.suppressed_event_country_codes;
          }
          return JSON.stringify(parsed);
        }
      } catch { /* fall through to raw */ }
      return rawParamsJson;
    }
    if (ruleType === 'log_type_match') {
      const params = { log_type: logType };
      if (matchConditions.length > 0) {
        params.match_conditions = matchConditions.filter((c) => c.field);
      }
      return JSON.stringify(stampScope(params));
    }
    if (ruleType === 'baseline_deviation') {
      return JSON.stringify(stampScope({
        baseline_field: baselineField,
        event_types: eventTypes.length > 0 ? eventTypes : ['signin'],
      }));
    }
    if (ruleType === 'operation_baseline_deviation') {
      // Pipeline reads plural `operations` (array). Comma-split the textbox.
      // Spread the clone-source params first so unknown keys (e.g. office_ip_threshold,
      // hand-tuned thresholds set by the pipeline team) survive a clone-and-save.
      const opsArr = opBaselineOperation
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      let original = {};
      try { original = JSON.parse(draft?.parametersJson || '{}'); } catch { /* ignore */ }
      if (!original || typeof original !== 'object' || Array.isArray(original)) original = {};
      const params = { ...original };
      delete params.operation; // legacy singular form — replaced by plural operations
      delete params.suppression_tenant_id;
      delete params.excluded_ip_providers;
      delete params.baseline_window;
      delete params.baseline_ip_column;
      params.log_type       = opBaselineLogType;
      params.operations     = opsArr;
      params.baseline_field = opBaselineField;
      return JSON.stringify(stampScope(params));
    }
    return JSON.stringify(stampScope({
      ip_flag: ipFlag,
      event_types: eventTypes.length > 0 ? eventTypes : ['signin', 'graph_signin'],
    }));
  };

  const handleSave = async () => {
    setSaveError('');

    if (!ruleName.trim()) {
      setSaveError('Rule name is required.');
      return;
    }
    if (ruleType === 'ip_flag_match' && !ipFlag) {
      setSaveError('IP flag is required.');
      return;
    }
    if (ruleType === 'log_type_match' && !logType) {
      setSaveError('Log type is required.');
      return;
    }
    if (ruleType === 'baseline_deviation' && !baselineField) {
      setSaveError('Baseline field is required.');
      return;
    }
    if (ruleType === 'operation_baseline_deviation' && !opBaselineLogType) {
      setSaveError('Log type is required.');
      return;
    }
    if (ruleType === 'operation_baseline_deviation') {
      const opsArr = opBaselineOperation.split(',').map((s) => s.trim()).filter(Boolean);
      if (opsArr.length === 0) {
        setSaveError('At least one operation is required.');
        return;
      }
    }

    const payload = {
      ruleName: ruleName.trim(),
      description: description.trim(),
      severity,
      ruleType,
      pythonFunctionName: pythonFunctionName.trim() || undefined,
      parametersJson: buildParametersJson(),
      cooldownMinutes: Number(cooldownMinutes) || 0,
      suggestedSlug: suggestedSlug || undefined,
      skipIfEntraJoined,
      skipIfCompliantDevice,
      skipIfDrmmMatch,
      skipIfIntuneManaged,
      skipIfInTenantBaseline,
      skipIfInUserBaseline,
      skipIfProviderInUserBaseline,
      skipIfCtapTrust,
      skipIfInMailboxPairAllowlist,
      skipIfTeamsPhoneEnrollment,
      skipIfInFileHashAllowlist,
      skipIfInDefenderPassthroughIpAllowlist,
      skipIfInUserAppPairAllowlist,
      skipIfInGroupAppAllowlist,
      skipIpv6PrefixMatch,
      overrideIfMaliciousIp,
      dedupOnEventId,
      includedTenantIds: includedTenantIds.join(','),
      excludedTenantIds: excludedTenantIds.join(','),
      includedMspCodes: includedMspCodes.join(','),
      excludedMspCodes: excludedMspCodes.join(','),
    };

    setPhase('saving');
    try {
      await createRule(payload);
      onSuccess();
      handleClose();
    } catch (err) {
      if (err.status === 400) {
        setSaveError(err.message || 'Validation failed.');
        setPhase('draft_review');
      } else {
        setErrorMessage(err.message || 'Failed to create rule.');
        setPhase('error_panel');
      }
    }
  };

  const toggleEventType = (et) => {
    setEventTypes((prev) =>
      prev.includes(et) ? prev.filter((t) => t !== et) : [...prev, et]
    );
  };

  const updateMatchCondition = (idx, field, value) => {
    setMatchConditions((prev) => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
  };

  const addMatchCondition = () => {
    setMatchConditions((prev) => [...prev, { field: '', operator: 'equals', value: '' }]);
  };

  const removeMatchCondition = (idx) => {
    setMatchConditions((prev) => prev.filter((_, i) => i !== idx));
  };

  const addCountryCode = () => {
    const upper = countryInput.trim().toUpperCase();
    if (upper.length !== 2 || !/^[A-Z]{2}$/.test(upper)) return;
    setSuppressedCountryCodes((prev) => (prev.includes(upper) ? prev : [...prev, upper]));
    setCountryInput('');
  };

  const removeCountryCode = (code) => {
    setSuppressedCountryCodes((prev) => prev.filter((c) => c !== code));
  };

  if (!isOpen) return null;

  const outcome = generateResponse?.outcome;
  const serverValidation = generateResponse?.serverValidation;
  const preClassification = generateResponse?.preClassification;
  const confidence = outcome?.confidence;
  const confLabel = CONFIDENCE_LABEL[confidence] || CONFIDENCE_LABEL.medium;
  const isSaving = phase === 'saving';

  const formatRetryTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-40" onClick={handleClose} />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-[520px] bg-gray-900 border-l border-gray-800 shadow-2xl z-50 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <div className="flex items-center gap-2">
            {cloneSource
              ? <DocumentDuplicateIcon className="h-5 w-5 text-sky-400" />
              : <SparklesIcon className="h-5 w-5 text-purple-400" />}
            <h2 className="text-lg font-semibold text-gray-100">{cloneSource ? 'Clone Rule' : 'Create Rule'}</h2>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-200 transition-colors">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* ── INPUT FORM ── */}
          {phase === 'input_form' && (
            <>
              <p className="text-sm text-gray-400">
                Describe the detection you want, or paste a Sentinel YAML rule. AI will generate a draft rule for review.
              </p>

              {/* Input type toggle */}
              <div className="flex gap-2">
                {[{ value: 'natural_language', label: 'Natural language' }, { value: 'yaml', label: 'Sentinel YAML' }].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setInputType(opt.value)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                      inputType === opt.value
                        ? 'bg-purple-900/40 text-purple-300 border-purple-700/50'
                        : 'bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Workload picker */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Workload (optional)</label>
                <select
                  value={workload}
                  onChange={(e) => setWorkload(e.target.value)}
                  className="input-field w-full"
                >
                  <option value="">Auto-detect from description</option>
                  {LOG_TYPE_OPTIONS.map((lt) => (
                    <option key={lt.value} value={lt.value}>{lt.label} ({lt.value})</option>
                  ))}
                </select>
                <p className="text-xs text-gray-600 mt-1">Constrains the AI to generate rules for this data source.</p>
              </div>

              {/* Text area */}
              <div>
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  rows={inputType === 'yaml' ? 12 : 5}
                  placeholder={inputType === 'yaml'
                    ? 'Paste Sentinel analytics rule YAML here...'
                    : 'e.g. "Alert me when someone signs in from a VPN" or "Detect leaked credential risk detections"'
                  }
                  className="input-field w-full resize-y font-mono text-sm"
                  maxLength={INPUT_LIMITS[inputType]}
                />
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-gray-600">
                    {inputType === 'natural_language' ? 'Max 5,000 chars' : 'Max 20,000 chars'}
                  </span>
                  <span className="text-xs text-gray-600">
                    {inputText.length.toLocaleString()} / {INPUT_LIMITS[inputType].toLocaleString()}
                  </span>
                </div>
              </div>
            </>
          )}

          {/* ── LOADING ── */}
          {phase === 'loading' && (
            <div className="flex flex-col items-center justify-center py-16">
              <ArrowPathIcon className="h-8 w-8 text-purple-400 animate-spin mb-4" />
              <p className="text-sm text-gray-400">Generating rule draft...</p>
              <p className="text-xs text-gray-600 mt-1">This may take 3-10 seconds</p>
            </div>
          )}

          {/* ── DRAFT REVIEW ── */}
          {(phase === 'draft_review' || phase === 'saving') && (
            <>
              {/* Clone source banner */}
              {cloneSource && (
                <div className="flex items-center gap-2 p-3 bg-sky-900/20 border border-sky-700/30 rounded-lg">
                  <DocumentDuplicateIcon className="h-4 w-4 text-sky-400 flex-shrink-0" />
                  <p className="text-xs text-sky-300">
                    Cloning from <span className="font-mono text-sky-200">{cloneSource.ruleId}</span> — edit fields below, then save as a new TEST rule.
                  </p>
                </div>
              )}

              {/* Confidence badge (AI path only) */}
              {!cloneSource && (
                <Badge tone={confidenceTone(confidence)}>
                  {confidence === 'high' && <CheckCircleIcon className="h-3.5 w-3.5" />}
                  {(confidence === 'medium' || confidence === 'low') && <ExclamationTriangleIcon className="h-3.5 w-3.5" />}
                  {confLabel}
                </Badge>
              )}

              {/* Generation duration (AI path only) */}
              {!cloneSource && generateResponse?.meta?.durationMs && (
                <span className="text-xs text-gray-600 ml-2">Generated in {(generateResponse.meta.durationMs / 1000).toFixed(1)}s</span>
              )}

              {/* PreClassification info (YAML only) */}
              {preClassification && (
                <div className="p-3 bg-gray-800/50 border border-gray-700/50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Pre-Classification</p>
                  <p className="text-sm text-gray-400">
                    Classification: <span className="text-gray-300 font-medium">{preClassification.classification}</span>
                  </p>
                  {preClassification.droppedTables?.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Unavailable Sentinel tables: {preClassification.droppedTables.join(', ')}
                    </p>
                  )}
                </div>
              )}

              {/* Mapping explanation */}
              {outcome?.mappingExplanation && (
                <div className="p-3 bg-gray-800/50 border border-gray-700/50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">AI Explanation</p>
                  <p className="text-sm text-gray-300">{outcome.mappingExplanation}</p>
                </div>
              )}

              {/* Validation notes */}
              {outcome?.validationNotes?.length > 0 && (
                <div className="p-3 bg-blue-900/20 border border-blue-700/30 rounded-lg">
                  <p className="text-xs text-blue-400 font-medium mb-1">Notes</p>
                  <ul className="list-disc list-inside text-xs text-blue-300 space-y-0.5">
                    {outcome.validationNotes.map((n, i) => <li key={i}>{n}</li>)}
                  </ul>
                </div>
              )}

              {/* Server validation errors */}
              {serverValidation?.errors?.length > 0 && (
                <div className="p-3 bg-red-900/30 border border-red-700/50 rounded-lg">
                  <p className="text-xs text-red-400 font-medium mb-1">Validation Errors</p>
                  <ul className="list-disc list-inside text-xs text-red-300 space-y-0.5">
                    {serverValidation.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}

              {/* Server validation warnings */}
              {serverValidation?.warnings?.length > 0 && (
                <div className="p-3 bg-amber-900/20 border border-amber-700/30 rounded-lg">
                  <p className="text-xs text-amber-400 font-medium mb-1">Warnings</p>
                  <ul className="list-disc list-inside text-xs text-amber-300 space-y-0.5">
                    {serverValidation.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}

              <hr className="border-gray-800" />

              {/* Rule type badge */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Rule Type:</span>
                <span className="px-2 py-0.5 text-xs font-medium rounded border bg-gray-800 text-gray-300 border-gray-700">
                  {ruleType === 'ip_flag_match' ? 'IP Flag Match' : ruleType === 'log_type_match' ? 'Log Type Match' : ruleType === 'baseline_deviation' ? 'Baseline Deviation' : ruleType === 'operation_baseline_deviation' ? 'Op + Baseline' : ruleType === 'python_function_config' ? 'Config Function' : ruleType}
                </span>
              </div>

              {/* Editable fields */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">
                  Rule Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={ruleName}
                  onChange={(e) => setRuleName(e.target.value)}
                  className="input-field w-full"
                  maxLength={100}
                  disabled={isSaving}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="input-field w-full resize-y"
                  disabled={isSaving}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Severity</label>
                <select value={severity} onChange={(e) => setSeverity(e.target.value)} className="input-field w-48" disabled={isSaving}>
                  {SEVERITY_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Cooldown (minutes)</label>
                <input
                  type="number"
                  min="0"
                  max="1440"
                  value={cooldownMinutes}
                  onChange={(e) => setCooldownMinutes(e.target.value)}
                  className="input-field w-32"
                  disabled={isSaving}
                />
              </div>

              {/* Alert de-dup granularity — interacts with cooldown, so it sits next to it. */}
              <div className="flex items-center justify-between">
                <div className="pr-4">
                  <label className="block text-sm font-medium text-gray-300">Alert on every matching event</label>
                  <p className="text-xs text-gray-500">
                    When off (default), repeated matches for the same user collapse to one alert per
                    cooldown window. Turn on only for rules that should surface every distinct source
                    event. With this on, the cooldown does not collapse per-user duplicates.
                  </p>
                </div>
                <button
                  onClick={() => setDedupOnEventId(!dedupOnEventId)}
                  disabled={isSaving}
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                    dedupOnEventId ? 'bg-blue-600' : 'bg-gray-700'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    dedupOnEventId ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {/* Rule Scope — tenant include + tenant exclusion + IP provider exclusion */}
              <div className="space-y-3">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">Rule Scope</label>

                {/* MSP scope — include/exclude by MSP code (all current & future tenants of the MSP).
                    Hidden for the batched mailbox rule (first-entry semantics would apply batch-wide). */}
                {pythonFunctionName === MAILBOX_NON_OWNER_FN ? (
                  <p className="text-xs text-gray-500 italic">
                    MSP scoping is not available on this batched rule (it coalesces per-tenant entries, so an MSP code would apply batch-wide). Use the tenant lists instead.
                  </p>
                ) : (
                  <>
                    <MspScopeSelect
                      label="Scope to these MSPs (include)"
                      help="Applies this rule to every current and future tenant of the selected MSP(s). Combined with the include-tenant list as OR. An include that resolves to no tenants matches nothing."
                      selected={includedMspCodes}
                      setSelected={setIncludedMspCodes}
                      catalog={user?.mspCatalog}
                      isProd={false}
                      tone="sky"
                    />
                    <MspScopeSelect
                      label="Exclude these MSPs"
                      help="Drops matches from every tenant of the selected MSP(s) before alert generation."
                      selected={excludedMspCodes}
                      setSelected={setExcludedMspCodes}
                      catalog={user?.mspCatalog}
                      isProd={false}
                      tone="amber"
                    />
                  </>
                )}

                {/* Included tenants — multi-select */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Include tenants</label>
                  <p className="text-xs text-gray-500 mb-2">
                    Empty = rule applies to all tenants. Non-empty = rule applies only to the selected tenants. Can coexist with Exclude; when both are set, Include narrows first and Exclude drops second.
                  </p>
                  {includedTenantIds.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {includedTenantIds.map((tid) => {
                        const t = tenantList.find((x) => x.TenantID === tid);
                        const label = t ? (t.FriendlyName || tid) : tid;
                        const title = t ? `${t.FriendlyName || '(unnamed)'} — ${tid}` : tid;
                        return (
                          <span key={tid} title={title} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-sky-900/30 text-sky-300 border border-sky-700/40 text-xs">
                            <span>{label}</span>
                            <button
                              type="button"
                              onClick={() => setIncludedTenantIds(includedTenantIds.filter((x) => x !== tid))}
                              className="text-sky-400 hover:text-sky-200"
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <select
                    value=""
                    onChange={(e) => {
                      const tid = e.target.value;
                      if (tid && !includedTenantIds.includes(tid)) {
                        setIncludedTenantIds([...includedTenantIds, tid]);
                      }
                    }}
                    className="input-field w-full"
                  >
                    <option value="">— add tenant —</option>
                    {[...tenantList]
                      .filter((t) => !includedTenantIds.includes(t.TenantID))
                      .sort((a, b) => (a.FriendlyName || '').localeCompare(b.FriendlyName || ''))
                      .map((t) => (
                        <option key={t.TenantID} value={t.TenantID}>
                          {t.FriendlyName || t.TenantID}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Excluded tenants — multi-select */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Exclude tenants</label>
                  <p className="text-xs text-gray-500 mb-2">
                    Matches from tenants in this list prevents the alert.
                  </p>
                  {excludedTenantIds.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {excludedTenantIds.map((tid) => {
                        const t = tenantList.find((x) => x.TenantID === tid);
                        const label = t ? (t.FriendlyName || tid) : tid;
                        const title = t ? `${t.FriendlyName || '(unnamed)'} — ${tid}` : tid;
                        return (
                          <span key={tid} title={title} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-900/30 text-amber-300 border border-amber-700/40 text-xs">
                            <span>{label}</span>
                            <button
                              type="button"
                              onClick={() => setExcludedTenantIds(excludedTenantIds.filter((x) => x !== tid))}
                              className="text-amber-400 hover:text-amber-200"
                            >
                              ×
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <select
                    value=""
                    onChange={(e) => {
                      const tid = e.target.value;
                      if (tid && !excludedTenantIds.includes(tid)) {
                        setExcludedTenantIds([...excludedTenantIds, tid]);
                      }
                    }}
                    className="input-field w-full"
                  >
                    <option value="">— add tenant —</option>
                    {[...tenantList]
                      .filter((t) => !excludedTenantIds.includes(t.TenantID))
                      .sort((a, b) => (a.FriendlyName || '').localeCompare(b.FriendlyName || ''))
                      .map((t) => (
                        <option key={t.TenantID} value={t.TenantID}>
                          {t.FriendlyName || t.TenantID}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Excluded IP providers — chip/tag input */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Exclude IPs from these providers</label>
                  <p className="text-xs text-gray-500 mb-2">
                    Matches whose IP enrichment shows any of these providers will be suppressed. Case-insensitive exact match — use the real provider name as it appears in IP enrichment (no wildcards). Press Enter to add.
                  </p>
                  {excludedIpProviders.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {excludedIpProviders.map((p, i) => (
                        <span key={`${p}-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-900/30 text-amber-300 border border-amber-700/40 text-xs font-mono">
                          <span>{p}</span>
                          <button
                            type="button"
                            onClick={() => setExcludedIpProviders(excludedIpProviders.filter((_, idx) => idx !== i))}
                            className="text-amber-400 hover:text-amber-200"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <input
                    type="text"
                    value={providerInput}
                    placeholder='e.g. Zscaler, Inc. — press Enter to add'
                    onChange={(e) => setProviderInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && providerInput.trim()) {
                        e.preventDefault();
                        const v = providerInput.trim();
                        if (!excludedIpProviders.some((x) => x.toLowerCase() === v.toLowerCase())) {
                          setExcludedIpProviders([...excludedIpProviders, v]);
                        }
                        setProviderInput('');
                      }
                    }}
                    className="input-field w-full"
                  />
                </div>
              </div>

              {/* Suppression toggles */}
              <div className="space-y-3">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">Suppression</label>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-300">Skip if Entra Joined</label>
                    <p className="text-xs text-gray-500">Suppress alerts for Azure AD-joined and Hybrid Azure AD-joined devices.</p>
                  </div>
                  <button
                    onClick={() => setSkipIfEntraJoined(!skipIfEntraJoined)}
                    disabled={isSaving}
                    className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors disabled:opacity-50 ${
                      skipIfEntraJoined ? 'bg-blue-600' : 'bg-gray-700'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      skipIfEntraJoined ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-300">Skip if Intune Managed</label>
                    <p className="text-xs text-gray-500">Suppress alerts for Intune-enrolled devices (compliant or not).</p>
                  </div>
                  <button
                    onClick={() => setSkipIfIntuneManaged(!skipIfIntuneManaged)}
                    disabled={isSaving}
                    className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors disabled:opacity-50 ${
                      skipIfIntuneManaged ? 'bg-blue-600' : 'bg-gray-700'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      skipIfIntuneManaged ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-300">Skip if Intune Compliant Device</label>
                    <p className="text-xs text-gray-500">Suppress alerts when the event originates from a compliant device.</p>
                  </div>
                  <button
                    onClick={() => setSkipIfCompliantDevice(!skipIfCompliantDevice)}
                    disabled={isSaving}
                    className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors disabled:opacity-50 ${
                      skipIfCompliantDevice ? 'bg-blue-600' : 'bg-gray-700'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      skipIfCompliantDevice ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-300">Skip if DRMM Match</label>
                    <p className="text-xs text-gray-500">Suppress alerts when the user has a DRMM-managed device.</p>
                  </div>
                  <button
                    onClick={() => setSkipIfDrmmMatch(!skipIfDrmmMatch)}
                    disabled={isSaving}
                    className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors disabled:opacity-50 ${
                      skipIfDrmmMatch ? 'bg-blue-600' : 'bg-gray-700'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      skipIfDrmmMatch ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-300">Skip if IP in MSP Tenant Baseline</label>
                    <p className="text-xs text-gray-500">Suppress when event's IP is in MSP Tenant 7-day Baseline</p>
                  </div>
                  <button
                    onClick={() => setSkipIfInTenantBaseline(!skipIfInTenantBaseline)}
                    disabled={isSaving}
                    className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors disabled:opacity-50 ${
                      skipIfInTenantBaseline ? 'bg-blue-600' : 'bg-gray-700'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      skipIfInTenantBaseline ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-300">Skip if IP in User Baseline</label>
                    <p className="text-xs text-gray-500">Suppress when event's IP is in the sign-in user's own baseline</p>
                  </div>
                  <button
                    onClick={() => setSkipIfInUserBaseline(!skipIfInUserBaseline)}
                    disabled={isSaving}
                    className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors disabled:opacity-50 ${
                      skipIfInUserBaseline ? 'bg-blue-600' : 'bg-gray-700'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      skipIfInUserBaseline ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-300">Skip if Provider in User Baseline</label>
                    <p className="text-xs text-gray-500">Suppress when the event IP's provider/ASN is already established in the sign-in user's own baseline (rotating VPN/egress)</p>
                  </div>
                  <button
                    onClick={() => setSkipIfProviderInUserBaseline(!skipIfProviderInUserBaseline)}
                    disabled={isSaving}
                    className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors disabled:opacity-50 ${
                      skipIfProviderInUserBaseline ? 'bg-blue-600' : 'bg-gray-700'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      skipIfProviderInUserBaseline ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                {skipIfProviderInUserBaseline && (
                <div className="flex items-center justify-between pl-4 border-l-2 border-gray-700">
                  <div>
                    <label className="block text-xs font-medium text-gray-400">Min distinct baseline IPs per provider</label>
                    <p className="text-xs text-gray-600">How many baseline IPs must back a provider before it counts as established (default 2)</p>
                  </div>
                  <input
                    type="number"
                    min="1"
                    value={providerBaselineMinIps}
                    disabled={isSaving}
                    onChange={(e) => setProviderBaselineMinIps(e.target.value === '' ? '' : Number(e.target.value))}
                    className="input-field w-20 ml-4 disabled:opacity-50"
                  />
                </div>
                )}
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-300">Skip if CTAP Inbound Trust</label>
                    <p className="text-xs text-gray-500">Honor resource tenant's CTAP inbound trust.<br />(MFA, compliant device, & hybrid join)</p>
                  </div>
                  <button
                    onClick={() => setSkipIfCtapTrust(!skipIfCtapTrust)}
                    disabled={isSaving}
                    className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors disabled:opacity-50 ${
                      skipIfCtapTrust ? 'bg-blue-600' : 'bg-gray-700'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      skipIfCtapTrust ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-300">Skip if in Mailbox-Pair Allowlist</label>
                    <p className="text-xs text-gray-500">Drops when every accessed (owner, actor) mailbox pair is on the Mailbox-Pair Allowlist.</p>
                  </div>
                  <button
                    onClick={() => setSkipIfInMailboxPairAllowlist(!skipIfInMailboxPairAllowlist)}
                    disabled={isSaving}
                    className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors disabled:opacity-50 ${
                      skipIfInMailboxPairAllowlist ? 'bg-blue-600' : 'bg-gray-700'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      skipIfInMailboxPairAllowlist ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-300">Suppress Teams-phone device-code enrollments</label>
                    <p className="text-xs text-gray-500">Drops a device-code sign-in when the account is allowlisted AND a Teams-phone registered within ~2 min. Malicious IPs still alert.</p>
                  </div>
                  <button
                    onClick={() => setSkipIfTeamsPhoneEnrollment(!skipIfTeamsPhoneEnrollment)}
                    disabled={isSaving}
                    className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors disabled:opacity-50 ${
                      skipIfTeamsPhoneEnrollment ? 'bg-blue-600' : 'bg-gray-700'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      skipIfTeamsPhoneEnrollment ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-300">Suppress allowlisted (file hash, threat) Defender alerts</label>
                    <p className="text-xs text-gray-500">Drops a Defender alert when a file-evidence hash is on the File-Hash Allowlist for the threat family (or a <span className="font-mono">*</span> entry). Malicious IPs still alert. <a href="/settings/defender-file-hash-allowlist" className="text-blue-400 hover:text-blue-300">View list →</a></p>
                  </div>
                  <button
                    onClick={() => setSkipIfInFileHashAllowlist(!skipIfInFileHashAllowlist)}
                    disabled={isSaving}
                    className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors disabled:opacity-50 ${
                      skipIfInFileHashAllowlist ? 'bg-blue-600' : 'bg-gray-700'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      skipIfInFileHashAllowlist ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-300">Suppress Microsoft-security alerts from allowlisted (benign) IPs</label>
                    <p className="text-xs text-gray-500">When on, a Microsoft-security pass-through alert is suppressed if its source IP (sign-in IP or device IP) matches an entry in the Defender Pass-through IP Allowlist. Global across tenants. A known-malicious IP still alerts. <a href="/settings/benign-ip-allowlist" className="text-blue-400 hover:text-blue-300">View list →</a></p>
                  </div>
                  <button
                    onClick={() => setSkipIfInDefenderPassthroughIpAllowlist(!skipIfInDefenderPassthroughIpAllowlist)}
                    disabled={isSaving}
                    className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors disabled:opacity-50 ${
                      skipIfInDefenderPassthroughIpAllowlist ? 'bg-blue-600' : 'bg-gray-700'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      skipIfInDefenderPassthroughIpAllowlist ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-300">Suppress allowlisted (user, application) pairs</label>
                    <p className="text-xs text-gray-500">Drops a match whose (user, app) is on the User/App Allowlist (matched by app GUID). Malicious IPs still alert. <a href="/settings/user-app-pair-allowlist" className="text-blue-400 hover:text-blue-300">View list →</a></p>
                  </div>
                  <button
                    onClick={() => setSkipIfInUserAppPairAllowlist(!skipIfInUserAppPairAllowlist)}
                    disabled={isSaving}
                    className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors disabled:opacity-50 ${
                      skipIfInUserAppPairAllowlist ? 'bg-blue-600' : 'bg-gray-700'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      skipIfInUserAppPairAllowlist ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-300">Suppress allowlisted (group, application) pairs</label>
                    <p className="text-xs text-gray-500">Drops a match when the user is in an allowlisted group AND the app matches (membership refreshed daily). Malicious IPs still alert. <a href="/settings/group-app-allowlist" className="text-blue-400 hover:text-blue-300">View list →</a></p>
                  </div>
                  <button
                    onClick={() => setSkipIfInGroupAppAllowlist(!skipIfInGroupAppAllowlist)}
                    disabled={isSaving}
                    className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors disabled:opacity-50 ${
                      skipIfInGroupAppAllowlist ? 'bg-blue-600' : 'bg-gray-700'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      skipIfInGroupAppAllowlist ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-300">Match IPv6 Addresses by /64 Prefix</label>
                    <p className="text-xs text-gray-500">Widens IPv6 comparison from exact to /64 prefix (same network).</p>
                  </div>
                  <button
                    onClick={() => setSkipIpv6PrefixMatch(!skipIpv6PrefixMatch)}
                    disabled={isSaving}
                    className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors disabled:opacity-50 ${
                      skipIpv6PrefixMatch ? 'bg-blue-600' : 'bg-gray-700'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      skipIpv6PrefixMatch ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-300">Alert if a Known Malicious IP</label>
                    <p className="text-xs text-gray-500">
                      Fire the alert even when another Option would drop it.{' '}
                      <Link to="/settings/known-malicious-ips" className="text-blue-400 hover:text-blue-300 underline">
                        Manage list →
                      </Link>
                    </p>
                  </div>
                  <button
                    onClick={() => setOverrideIfMaliciousIp(!overrideIfMaliciousIp)}
                    disabled={isSaving}
                    className={`relative inline-flex h-6 w-11 shrink-0 ml-4 items-center rounded-full transition-colors disabled:opacity-50 ${
                      overrideIfMaliciousIp ? 'bg-blue-600' : 'bg-gray-700'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      overrideIfMaliciousIp ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>

              {/* Baseline window / IP matching mode — only relevant when user-baseline suppression is active */}
              {skipIfInUserBaseline && (
              <div className="space-y-3">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">Baseline</label>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Baseline window</label>
                  <p className="text-xs text-gray-500 mb-2">
                    How much sign-in history counts as "normal" for this user. 30-day captures occasional client sites, hotel Wi-Fi, and reduces false positives on "new IP" rules.
                  </p>
                  <select
                    value={baselineWindow}
                    disabled={isSaving}
                    onChange={(e) => setBaselineWindow(e.target.value)}
                    className="input-field w-full"
                  >
                    {BASELINE_WINDOW_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              )}

              <hr className="border-gray-800" />

              {/* Parameters editor */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-2">Parameters</label>

                {pythonFunctionName === NEW_COUNTRY_SIGNIN_FN && (
                  <div className="mb-4 p-3 rounded border border-amber-700/30 bg-amber-900/10">
                    <label className="block text-xs text-gray-400 mb-1">Suppressed Event Countries</label>
                    <p className="text-xs text-gray-500 mb-2">
                      Suppress new-country alerts when the event's country is in this list, regardless of baseline state.
                      ISO 2-letter codes (e.g. <span className="font-mono text-gray-300">US</span>, <span className="font-mono text-gray-300">GB</span>).
                      If <span className="font-mono text-gray-300">override_if_malicious_ip</span> is enabled on this rule, alerts whose source IP is in <span className="font-mono text-gray-300">KnownMaliciousIPs</span> still fire — threat intel takes precedence.
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        list="create-country-codes-list"
                        value={countryInput}
                        onChange={(e) => setCountryInput(e.target.value.toUpperCase().slice(0, 2))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ',') {
                            e.preventDefault();
                            addCountryCode();
                          }
                        }}
                        placeholder="ISO-2"
                        maxLength={2}
                        className="input-field w-24 font-mono uppercase"
                        disabled={isSaving}
                      />
                      <datalist id="create-country-codes-list">
                        {COMMON_COUNTRY_CODES.map((c) => <option key={c} value={c} />)}
                      </datalist>
                      <button
                        type="button"
                        onClick={addCountryCode}
                        disabled={isSaving || countryInput.trim().length !== 2}
                        className="btn-secondary text-xs px-3 disabled:opacity-50"
                      >
                        Add
                      </button>
                    </div>
                    {suppressedCountryCodes.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {suppressedCountryCodes.map((code) => (
                          <span
                            key={code}
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono rounded bg-amber-900/30 border border-amber-700/40 text-amber-300"
                          >
                            {code}
                            <button
                              type="button"
                              onClick={() => removeCountryCode(code)}
                              className="text-amber-400 hover:text-amber-200"
                              title={`Remove ${code}`}
                              disabled={isSaving}
                            >
                              <XMarkIcon className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {ruleType === 'ip_flag_match' && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">IP Flag <span className="text-red-400">*</span></label>
                      <select value={ipFlag} onChange={(e) => setIpFlag(e.target.value)} className="input-field w-full" disabled={isSaving}>
                        <option value="">— Select —</option>
                        {IP_FLAG_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label} ({opt.value})</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Event Types</label>
                      <div className="flex gap-4">
                        {EVENT_TYPE_OPTIONS.map((et) => (
                          <label key={et} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={eventTypes.includes(et)}
                              onChange={() => toggleEventType(et)}
                              className="rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500"
                              disabled={isSaving}
                            />
                            {et}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {ruleType === 'baseline_deviation' && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Baseline Field <span className="text-red-400">*</span></label>
                      <select value={baselineField} onChange={(e) => setBaselineField(e.target.value)} className="input-field w-full" disabled={isSaving}>
                        <option value="country_list">Country List — flag sign-ins from unknown countries</option>
                        <option value="signin_ip_list">Sign-in IP List — flag sign-ins from unknown IPs</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Event Types</label>
                      <div className="flex gap-4">
                        {EVENT_TYPE_OPTIONS.map((et) => (
                          <label key={et} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={eventTypes.includes(et)}
                              onChange={() => toggleEventType(et)}
                              className="rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500"
                              disabled={isSaving}
                            />
                            {et}
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-gray-600 mt-1">Which sign-in log shapes this rule evaluates against the baseline.</p>
                    </div>
                  </div>
                )}

                {ruleType === 'operation_baseline_deviation' && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Log Type <span className="text-red-400">*</span></label>
                      <select value={opBaselineLogType} onChange={(e) => setOpBaselineLogType(e.target.value)} className="input-field w-full" disabled={isSaving}>
                        <option value="">— Select —</option>
                        {LOG_TYPE_OPTIONS.map((lt) => (
                          <option key={lt.value} value={lt.value}>{lt.label} ({lt.value})</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Operations <span className="text-red-400">*</span></label>
                      <input
                        type="text"
                        value={opBaselineOperation}
                        onChange={(e) => setOpBaselineOperation(e.target.value)}
                        placeholder="e.g. New-InboxRule, Set-InboxRule, UpdateInboxRules"
                        className="input-field w-full"
                        disabled={isSaving}
                      />
                      <p className="text-xs text-gray-600 mt-1">Comma-separated. Matches any of the listed operations.</p>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Baseline Field</label>
                      <p className="text-sm text-gray-400 bg-gray-800/50 px-3 py-2 rounded border border-gray-700/50">Sign-in IP List</p>
                      <p className="text-xs text-gray-600 mt-1">Events are flagged when the client IP is not in the user's historical sign-in IP baseline across all device types.</p>
                    </div>
                  </div>
                )}

                {ruleType === 'log_type_match' && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Log Type <span className="text-red-400">*</span></label>
                      <select value={logType} onChange={(e) => setLogType(e.target.value)} className="input-field w-full" disabled={isSaving}>
                        <option value="">— Select —</option>
                        {LOG_TYPE_OPTIONS.map((lt) => (
                          <option key={lt.value} value={lt.value}>{lt.label} ({lt.value})</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Match Conditions</label>
                      {matchConditions.map((cond, idx) => (
                        <div key={idx} className="flex gap-2 mb-2 items-center">
                          <input
                            type="text"
                            value={cond.field}
                            onChange={(e) => updateMatchCondition(idx, 'field', e.target.value)}
                            placeholder="Field or $.jsonPath"
                            className="input-field flex-1 text-xs"
                            disabled={isSaving}
                          />
                          <select
                            value={cond.operator}
                            onChange={(e) => updateMatchCondition(idx, 'operator', e.target.value)}
                            className="input-field w-28 text-xs"
                            disabled={isSaving}
                          >
                            {MATCH_OPERATORS.map((op) => (
                              <option key={op} value={op}>{op}</option>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={cond.value}
                            onChange={(e) => updateMatchCondition(idx, 'value', e.target.value)}
                            placeholder="Value"
                            className="input-field flex-1 text-xs"
                            disabled={isSaving}
                          />
                          <button
                            onClick={() => removeMatchCondition(idx)}
                            className="text-red-400 hover:text-red-300 text-xs px-1"
                            disabled={isSaving}
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      {(() => {
                        const hint = getFieldSyntaxHint(logType);
                        return hint ? (
                          <div className="mb-2 flex items-start gap-1.5 text-xs text-gray-400">
                            <InformationCircleIcon className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-blue-400" />
                            <span>
                              <span className="font-medium text-gray-300">Field syntax: </span>
                              Top-level columns are case-insensitive (e.g. <code className="text-gray-300">UserId</code>).
                              JSON paths must be <span className="text-amber-300">{hint.caseStyle}</span> and prefixed with <code className="text-gray-300">$.</code> —
                              {' '}{hint.api} example: <code className="text-gray-300">{hint.example}</code>.
                            </span>
                          </div>
                        ) : null;
                      })()}
                      <button
                        onClick={addMatchCondition}
                        className="text-xs text-blue-400 hover:text-blue-300"
                        disabled={isSaving}
                      >
                        + Add condition
                      </button>
                    </div>
                  </div>
                )}
                {ruleType === 'python_function_config' && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Python Function Name <span className="text-red-400">*</span></label>
                      <input
                        type="text"
                        value={pythonFunctionName}
                        onChange={(e) => setPythonFunctionName(e.target.value)}
                        placeholder="e.g. detect_mailbox_non_owner"
                        className="input-field w-full font-mono text-sm"
                        disabled={isSaving}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Parameters JSON <span className="text-gray-600">(pipeline-defined)</span></label>
                      <textarea
                        value={rawParamsJson}
                        onChange={(e) => setRawParamsJson(e.target.value)}
                        rows={6}
                        className="input-field w-full font-mono text-xs resize-y"
                        disabled={isSaving}
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── UNSUPPORTED PANEL ── */}
          {phase === 'unsupported_panel' && (
            <div className="py-8 space-y-4">
              <div className="flex items-start gap-3 p-4 bg-amber-900/20 border border-amber-700/30 rounded-lg">
                <ExclamationTriangleIcon className="h-6 w-6 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-300">This detection cannot be created automatically</p>
                  {outcome?.mappingExplanation && (
                    <p className="text-sm text-gray-400 mt-2">{outcome.mappingExplanation}</p>
                  )}
                </div>
              </div>
              {outcome?.unsupportedDependencies?.length > 0 && (
                <div className="px-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Unsupported Dependencies</p>
                  <ul className="list-disc list-inside text-sm text-gray-400 space-y-1">
                    {outcome.unsupportedDependencies.map((d, i) => <li key={i}>{d}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* ── EXISTING FUNCTION PANEL ── */}
          {phase === 'existing_function_panel' && (
            <div className="py-8">
              <div className="flex items-start gap-3 p-4 bg-blue-900/20 border border-blue-700/30 rounded-lg">
                <InformationCircleIcon className="h-6 w-6 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-300">This detection is already covered</p>
                  {outcome?.mappingExplanation && (
                    <p className="text-sm text-gray-400 mt-2">{outcome.mappingExplanation}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── RATE LIMITED PANEL ── */}
          {phase === 'rate_limited' && (
            <div className="py-8">
              <div className="flex flex-col items-center gap-4 p-6 bg-amber-900/20 border border-amber-700/30 rounded-lg">
                <ClockIcon className="h-10 w-10 text-amber-400" />
                <p className="text-sm font-medium text-amber-300">Rate limit reached</p>
                <p className="text-sm text-gray-400 text-center">
                  Please try again in <span className="font-mono text-amber-300">{formatRetryTime(rateLimitSeconds)}</span>
                </p>
              </div>
            </div>
          )}

          {/* ── ERROR PANEL ── */}
          {phase === 'error_panel' && (
            <div className="py-8">
              <div className="flex items-start gap-3 p-4 bg-red-900/30 border border-red-700/50 rounded-lg">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-300">Rule generation failed</p>
                  {errorMessage && <p className="text-sm text-gray-400 mt-2">{errorMessage}</p>}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-800 px-6 py-4 space-y-3">
          {/* Save error in draft review */}
          {saveError && (phase === 'draft_review' || phase === 'saving') && (
            <div className="flex items-start gap-2 p-3 bg-red-900/30 border border-red-700/50 rounded-lg">
              <ExclamationTriangleIcon className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-300">{saveError}</p>
            </div>
          )}

          {/* Input form buttons */}
          {phase === 'input_form' && (
            <div className="flex gap-3">
              <button onClick={handleClose} className="flex-1 px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg border border-gray-700 transition-colors">
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                disabled={!inputText.trim()}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-500 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <SparklesIcon className="h-4 w-4" />
                Generate
              </button>
            </div>
          )}

          {/* Draft review buttons */}
          {(phase === 'draft_review' || phase === 'saving') && (
            <div className="flex gap-3">
              <button
                onClick={cloneSource ? handleClose : resetToInput}
                disabled={isSaving}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg border border-gray-700 transition-colors disabled:opacity-50"
              >
                {cloneSource ? 'Cancel' : 'Back'}
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || (serverValidation && !serverValidation.isValid)}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? 'Saving...' : 'Save as TEST'}
              </button>
            </div>
          )}

          {/* Unsupported / existing function buttons */}
          {(phase === 'unsupported_panel' || phase === 'existing_function_panel') && (
            <div className="flex gap-3">
              <button onClick={handleClose} className="flex-1 px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg border border-gray-700 transition-colors">
                Close
              </button>
              <button
                onClick={resetToInput}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-500 rounded-lg transition-colors"
              >
                Try Different Description
              </button>
            </div>
          )}

          {/* Rate limited — no action, auto-returns to input */}
          {phase === 'rate_limited' && (
            <div className="flex gap-3">
              <button onClick={handleClose} className="flex-1 px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg border border-gray-700 transition-colors">
                Close
              </button>
            </div>
          )}

          {/* Error panel buttons */}
          {phase === 'error_panel' && (
            <div className="flex gap-3">
              <button onClick={handleClose} className="flex-1 px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg border border-gray-700 transition-colors">
                Close
              </button>
              <button
                onClick={resetToInput}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-500 rounded-lg transition-colors"
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
